import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { SharingBtn } from "@/components/Sharing";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Check, X, ExternalLink, FileImage, Search } from "lucide-react";
import {
  acceptSellerDistribution,
  acceptSellerDistributionBetweenUnities,
  rejectSellerDistribution,
  rejectSellerDistributionBetweenUnities,
} from "@/Api/distributions";
import { getDistributionProcessFiles } from "@/Api/distributionsProcess";
import { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { DISTRIBUTION_PROCESS_STATUS, DISTRIBUTION_TYPE } from "@/constants/distributionStatus";

type FileType = {
  id: number;
  name: string | null;
  file: string;
  file_name: string;
  url: string;
  created_at: string;
};

type ProductType = {
  id: number;
  name: string;
  classification: string;
  quotas: number;
  distribution_type_id?: number;
  quota_quantity_utilized: number;
  distribution_process_uuid: string;
  distribution_process_status_id: number;
  distribution_process_status_name: string;
  seller_name: string;
  seller_email: string;
  created_by_name?: string;
  created_at?: string;
  updated_by?: string;
  updated_by_email?: string;
  updated_at?: string;
  files?: FileType[];
};

type ContestType = {
  id: string;
  name: string | null;
  lottery: string;
  lottery_color?: string;
  lottery_id?: number;
  products: ProductType[];
};

type GroupedDistributionsType = {
  contests: ContestType[];
};

export const TableData = forwardRef<
  { triggerAcceptAll: () => void },
  {
    groupedData: GroupedDistributionsType;
    pageActions: {
      update: () => void;
    };
  }
>(({ groupedData, pageActions }, ref) => {
  const [loadingActions, setLoadingActions] = useState<{
    [key: string]: boolean;
  }>({});
  const [showAcceptAllDialog, setShowAcceptAllDialog] = useState(false);
  const [isAcceptingAll, setIsAcceptingAll] = useState(false);

  useImperativeHandle(ref, () => ({
    triggerAcceptAll: () => {
      setShowAcceptAllDialog(true);
    },
  }));
  const [acceptDialog, setAcceptDialog] = useState<{
    uuid: string;
    productName: string;
    sellerName: string;
    sellerEmail: string;
    quotas: number;
    distributionTypeId?: number;
  } | null>(null);
  const [rejectDialog, setRejectDialog] = useState<{
    uuid: string;
    productName: string;
    sellerName: string;
    sellerEmail: string;
    quotas: number;
    distributionTypeId?: number;
  } | null>(null);
  const [modalKey, setModalKey] = useState<string>(`modal-${Date.now()}`);
  const [filesMap, setFilesMap] = useState<Record<number, FileType[]>>({});
  const [loadingFiles, setLoadingFiles] = useState<Record<string, boolean>>({});
  const [searchTerm, setSearchTerm] = useState("");

  const loadFilesForContest = async (contest: ContestType) => {
    const contestKey = contest.id;
    if (loadingFiles[contestKey]) return;

    const productIds = contest.products.map(p => p.id);
    if (productIds.length === 0) return;

    setLoadingFiles(prev => ({ ...prev, [contestKey]: true }));
    try {
      const response = await getDistributionProcessFiles(productIds);
      if (response.data?.status && response.data?.response) {
        const filesData = response.data.response as Record<string, { id: number; file_name: string; url: string }[]>;
        const newFilesMap: Record<number, FileType[]> = {};

        Object.entries(filesData).forEach(([processId, files]) => {
          newFilesMap[Number(processId)] = files.map(f => ({
            id: f.id,
            name: null,
            file: '',
            file_name: f.file_name,
            url: f.url,
            created_at: '',
          }));
        });

        setFilesMap(prev => ({ ...prev, ...newFilesMap }));
      }
    } catch (error) {
      console.error("Erro ao carregar arquivos:", error);
    } finally {
      setLoadingFiles(prev => ({ ...prev, [contestKey]: false }));
    }
  };

  const getProductFiles = (productId: number): FileType[] => {
    return filesMap[productId] || [];
  };

  const handleAcceptAll = async () => {
    const allPendingDistributions = groupedData.contests.flatMap((contest) =>
      contest.products.filter(
        (product) =>
          product.distribution_process_status_id ===
          DISTRIBUTION_PROCESS_STATUS.PENDING
      )
    );

    if (allPendingDistributions.length === 0) {
      toast.error("Nenhuma distribuição pendente encontrada.");
      return;
    }

    setIsAcceptingAll(true);
    let successCount = 0;
    let errorCount = 0;

    try {
      for (const product of allPendingDistributions) {
        if (!product.distribution_process_uuid) continue;

        try {
          if (product.distribution_type_id === DISTRIBUTION_TYPE.BETWEEN_SELLER) {
            await acceptSellerDistributionBetweenUnities(product.distribution_process_uuid);
          } else {
            await acceptSellerDistribution(product.distribution_process_uuid);
          }
          successCount++;
        } catch (error) {
          console.error(
            `Erro ao aceitar distribuição ${product.distribution_process_uuid}:`,
            error
          );
          errorCount++;
        }
      }

      if (successCount > 0) {
        toast.success(
          `${successCount} distribuição(ões) aceita(s) com sucesso!`
        );
        pageActions.update();
      }

      if (errorCount > 0) {
        toast.error(`${errorCount} distribuição(ões) falharam na aceitação.`);
      }
    } finally {
      setIsAcceptingAll(false);
      setShowAcceptAllDialog(false);
    }
  };

  const handleAcceptDistribution = async (
    uuid: string,
    productName: string,
    distributionTypeId?: number
  ) => {
    setLoadingActions((prev) => ({ ...prev, [`accept-${uuid}`]: true }));
    try {
      if (distributionTypeId === DISTRIBUTION_TYPE.BETWEEN_SELLER) {
        await acceptSellerDistributionBetweenUnities(uuid);
      } else {
        await acceptSellerDistribution(uuid);
      }
      toast.success(`Distribuição aceita: ${productName}`);
      pageActions.update();
    } catch (error) {
      toast.error("Erro ao aceitar distribuição");
    } finally {
      setLoadingActions((prev) => ({ ...prev, [`accept-${uuid}`]: false }));
      setAcceptDialog(null);
    }
  };

  const handleRejectDistribution = async (
    uuid: string,
    productName: string,
    distributionTypeId?: number
  ) => {
    setLoadingActions((prev) => ({ ...prev, [`reject-${uuid}`]: true }));
    try {
      if (distributionTypeId === DISTRIBUTION_TYPE.BETWEEN_SELLER) {
        await rejectSellerDistributionBetweenUnities(uuid);
      } else {
        await rejectSellerDistribution(uuid);
      }
      toast.success(`Distribuição rejeitada: ${productName}`);
      pageActions.update();
    } catch (error) {
      toast.error("Erro ao rejeitar distribuição");
    } finally {
      setLoadingActions((prev) => ({ ...prev, [`reject-${uuid}`]: false }));
      setRejectDialog(null);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleString("pt-BR");
  };

  useEffect(() => {
    setModalKey(`modal-${Date.now()}`);
  }, [groupedData]);

  return (
    <div className="flex flex-col gap-4">
      <Table className="bg-zinc-50 rounded dark:bg-[#2c2c2c]">
        <TableHeader>
          <TableRow>
            <TableHead>Concurso</TableHead>
            <TableHead>Loteria</TableHead>
            <TableHead className="text-center">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {groupedData.contests.map((contest) => (
            <TableRow key={`${contest.id}-${modalKey}`}>
              <TableCell>{contest.name || "Não informado"}</TableCell>
              <TableCell>
                <span
                  className="px-2 py-1 rounded text-white text-xs font-medium"
                  style={{ backgroundColor: contest.lottery_color || "#666" }}
                >
                  {contest.lottery}
                </span>
              </TableCell>
              <TableCell className="text-center">
                <div className="flex justify-center gap-1">
                  <SharingBtn
                    key={`sharing-${contest.id}-${modalKey}`}
                    labels={{
                      title: "Distribuições Recebidas",
                      description: "Veja as distribuições que você recebeu",
                    }}
                    params={{
                      func: async () => {
                        loadFilesForContest(contest);
                        return {
                          data: { response: contest },
                          ok: true,
                          status: 200,
                          statusText: "OK",
                          headers: {},
                          config: {} as any,
                        };
                      },
                      funcParams: contest.id,
                    }}
                    className="sm:max-w-[700px]"
                    render={() => {
                      const filteredProducts = contest.products.filter((product) => {
                        if (!searchTerm.trim()) return true;
                        const search = searchTerm.toLowerCase();
                        const productName = `${product.name} ${product.classification}`.toLowerCase();
                        return productName.includes(search);
                      });

                      return (
                      <div className="flex flex-col h-[70vh]">
                        {/* Header fixo */}
                        <div className="sticky top-0 bg-white dark:bg-gray-900 z-10 pb-4 space-y-4">
                          <div className="flex justify-between items-center gap-4">
                            <div className="flex items-center gap-4">
                              <h3 className="font-semibold text-lg">
                                Produtos recebidos:
                              </h3>
                              {/* Campo de busca */}
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-gray-700 dark:text-gray-300">B:</span>
                                <Input
                                  placeholder="Ex: 200"
                                  value={searchTerm}
                                  onChange={(e) => setSearchTerm(e.target.value)}
                                  className="w-32"
                                />
                                <Button
                                  variant="default"
                                  className="bg-gray-800 hover:bg-gray-900 text-white"
                                  onClick={() => {}}
                                >
                                  <Search className="h-4 w-4 mr-2" />
                                  Buscar
                                </Button>
                              </div>
                            </div>
                            <Button
                              variant="outline"
                              onClick={() => setShowAcceptAllDialog(true)}
                              disabled={isAcceptingAll}
                              className="bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
                            >
                              {isAcceptingAll ? (
                                <>
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" />
                                  Aceitando...
                                </>
                              ) : (
                                "Aceitar Tudo"
                              )}
                            </Button>
                          </div>
                          <div className="flex flex-col gap-2 items-center justify-center p-2 rounded-md" style={{
                            background: contest?.lottery_color || "transparent"
                          }}>
                            <h3 className="font-semibold text-lg text-white">
                              Concurso {contest.name}
                              <br />
                              <span className="text-sm text-white">
                                {contest.lottery}
                              </span>
                            </h3>
                          </div>
                        </div>

                        {/* Lista de produtos com scroll */}
                        <ScrollArea className="flex-1">
                          <div className="flex flex-col gap-4 pr-4">
                            {filteredProducts.length === 0 ? (
                              <div className="text-center py-8 text-gray-500">
                                Nenhum bolão encontrado com o termo "{searchTerm}"
                              </div>
                            ) : (
                            filteredProducts.map((product) => {
                              const isPreSale = product.distribution_type_id === DISTRIBUTION_TYPE.PRE_SALE;
                              return (
                              <div
                                key={product.id}
                                className={`border p-4 rounded ${isPreSale ? 'bg-yellow-50 border-yellow-300 dark:bg-yellow-900/20 dark:border-yellow-700' : ''}`}
                              >
                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="font-medium">
                                      {product.name} - {product.classification}
                                    </p>
                                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                                      <span className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300 px-2.5 py-0.5 rounded">
                                        {product.quotas} cotas recebidas
                                      </span>
                                      <span
                                        className={`px-2.5 py-0.5 rounded text-sm font-medium ${
                                          product.distribution_process_status_id ===
                                          DISTRIBUTION_PROCESS_STATUS.PENDING
                                            ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300"
                                            : product.distribution_process_status_id ===
                                              DISTRIBUTION_PROCESS_STATUS.ACCEPTED
                                            ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300"
                                            : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300"
                                        }`}
                                      >
                                        {
                                          product.distribution_process_status_name
                                        }
                                      </span>
                                      {isPreSale && (
                                        <span className="bg-yellow-400 text-yellow-900 px-2.5 py-0.5 rounded text-sm font-semibold">
                                          Pré-venda
                                        </span>
                                      )}
                                    </div>
                                    <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border">
                                      <div className="flex items-center gap-2 mb-2">
                                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">📅 Informações</span>
                                      </div>
                                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                                        <div className="text-gray-600 dark:text-gray-400">
                                          <span className="font-medium block text-xs text-gray-500">Criado por</span>
                                          {product.created_by_name}
                                        </div>
                                        {product.created_at && (
                                          <div className="text-gray-600 dark:text-gray-400">
                                            <span className="font-medium block text-xs text-gray-500">Data de criação</span>
                                            {formatDate(product.created_at)}
                                          </div>
                                        )}
                                        {(product.updated_by || (isPreSale && product.distribution_process_status_id === DISTRIBUTION_PROCESS_STATUS.ACCEPTED)) && (
                                          <div className="text-gray-600 dark:text-gray-400">
                                            <span className="font-medium block text-xs text-gray-500">
                                              {product.distribution_process_status_id === DISTRIBUTION_PROCESS_STATUS.ACCEPTED
                                                ? "Aceito por"
                                                : product.distribution_process_status_id === DISTRIBUTION_PROCESS_STATUS.REJECTED
                                                ? "Rejeitado por"
                                                : "Atualizado por"}
                                            </span>
                                            {isPreSale && product.distribution_process_status_id === DISTRIBUTION_PROCESS_STATUS.ACCEPTED
                                              ? <span className="font-semibold text-yellow-700 dark:text-yellow-400">AUTOMATICAMENTE</span>
                                              : product.updated_by}
                                          </div>
                                        )}
                                        {product.updated_at && (
                                          <div className="text-gray-600 dark:text-gray-400">
                                            <span className="font-medium block text-xs text-gray-500">Data</span>
                                            {formatDate(product.updated_at)}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    {/* Arquivos/Fotos do Bolão */}
                                    {(() => {
                                      const productFiles = getProductFiles(product.id);
                                      const isLoadingContestFiles = loadingFiles[contest.id];
                                      if (isLoadingContestFiles) {
                                        return (
                                          <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                                            <div className="flex items-center gap-2">
                                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" />
                                              <span className="text-sm text-blue-600">Carregando arquivos...</span>
                                            </div>
                                          </div>
                                        );
                                      }
                                      if (productFiles.length === 0) return null;
                                      return (
                                        <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                                          <div className="flex items-center gap-2 mb-2">
                                            <FileImage className="w-4 h-4 text-blue-600" />
                                            <span className="text-sm font-medium text-blue-700 dark:text-blue-400">
                                              Fotos do Bolão ({productFiles.length})
                                            </span>
                                          </div>
                                          <div className="space-y-1">
                                            {productFiles.map((file) => (
                                              <a
                                                key={file.id}
                                                href={file.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex items-center gap-2 p-2 bg-white dark:bg-gray-800 rounded border hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors"
                                              >
                                                <ExternalLink className="w-4 h-4 text-blue-600 flex-shrink-0" />
                                                <span className="text-sm text-blue-600 hover:underline truncate">
                                                  {file.file_name}
                                                </span>
                                              </a>
                                            ))}
                                          </div>
                                        </div>
                                      );
                                    })()}
                                  </div>
                                  {product.distribution_process_uuid &&
                                    product.distribution_process_status_id ===
                                      DISTRIBUTION_PROCESS_STATUS.PENDING && (
                                      <div className="flex gap-2">
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          className="text-green-600 border-green-600 hover:bg-green-50"
                                          disabled={
                                            loadingActions[
                                              `accept-${product.distribution_process_uuid}`
                                            ]
                                          }
                                          onClick={() =>
                                            setAcceptDialog({
                                              uuid: product.distribution_process_uuid,
                                              productName: `${product.name} - ${product.classification}`,
                                              sellerName: product.seller_name,
                                              sellerEmail: product.seller_email,
                                              quotas: product.quotas,
                                              distributionTypeId: product.distribution_type_id,
                                            })
                                          }
                                        >
                                          <Check className="w-4 h-4 mr-1" />
                                          Aceitar
                                        </Button>

                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          className="text-red-600 border-red-600 hover:bg-red-50"
                                          disabled={
                                            loadingActions[
                                              `reject-${product.distribution_process_uuid}`
                                            ]
                                          }
                                          onClick={() =>
                                            setRejectDialog({
                                              uuid: product.distribution_process_uuid,
                                              productName: `${product.name} - ${product.classification}`,
                                              sellerName: product.seller_name,
                                              sellerEmail: product.seller_email,
                                              quotas: product.quotas,
                                              distributionTypeId: product.distribution_type_id,
                                            })
                                          }
                                        >
                                          <X className="w-4 h-4 mr-1" />
                                          Rejeitar
                                        </Button>
                                      </div>
                                    )}
                                </div>
                              </div>
                            );
                            })
                            )}
                          </div>
                        </ScrollArea>
                      </div>
                    );
                    }}
                  />
                </div>
              </TableCell>
            </TableRow>
          ))}
          {groupedData.contests.length === 0 && (
            <TableRow>
              <TableCell colSpan={3} className="text-center">
                Nenhuma distribuição encontrada.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <Dialog
        open={!!acceptDialog}
        onOpenChange={(open) => !open && setAcceptDialog(null)}
      >
        <DialogContent className="z-[60]" style={{ zIndex: 60 }}>
          <DialogHeader>
            <DialogTitle>Confirmar Aceitação</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja aceitar a distribuição de{" "}
              <strong>{acceptDialog?.quotas} cotas</strong> do produto{" "}
              <strong>{acceptDialog?.productName}</strong>?
              <br />
              <br />
              <strong>Vendedor:</strong> {acceptDialog?.sellerName} (
              {acceptDialog?.sellerEmail})
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAcceptDialog(null)}>
              Cancelar
            </Button>
            <Button
              onClick={() =>
                acceptDialog &&
                handleAcceptDistribution(
                  acceptDialog.uuid,
                  acceptDialog.productName,
                  acceptDialog.distributionTypeId
                )
              }
              className="bg-green-600 hover:bg-green-700"
              disabled={loadingActions[`accept-${acceptDialog?.uuid}`]}
            >
              {loadingActions[`accept-${acceptDialog?.uuid}`]
                ? "Processando..."
                : "Confirmar Aceitação"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!rejectDialog}
        onOpenChange={(open) => !open && setRejectDialog(null)}
      >
        <DialogContent className="z-[60]" style={{ zIndex: 60 }}>
          <DialogHeader>
            <DialogTitle>Confirmar Rejeição</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja rejeitar a distribuição de{" "}
              <strong>{rejectDialog?.quotas} cotas</strong> do produto{" "}
              <strong>{rejectDialog?.productName}</strong>?
              <br />
              <br />
              <strong>Vendedor:</strong> {rejectDialog?.sellerName} (
              {rejectDialog?.sellerEmail})
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialog(null)}>
              Cancelar
            </Button>
            <Button
              onClick={() =>
                rejectDialog &&
                handleRejectDistribution(
                  rejectDialog.uuid,
                  rejectDialog.productName,
                  rejectDialog.distributionTypeId
                )
              }
              className="bg-red-600 hover:bg-red-700"
              disabled={loadingActions[`reject-${rejectDialog?.uuid}`]}
            >
              {loadingActions[`reject-${rejectDialog?.uuid}`]
                ? "Processando..."
                : "Confirmar Rejeição"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showAcceptAllDialog} onOpenChange={setShowAcceptAllDialog}>
        <DialogContent className="z-[70]" style={{ zIndex: 70 }}>
          <DialogHeader>
            <DialogTitle>Aceitar Todas as Distribuições Pendentes</DialogTitle>
            <DialogDescription>
              {(() => {
                const allPendingDistributions = groupedData.contests.flatMap(
                  (contest) =>
                    contest.products.filter(
                      (product) =>
                        product.distribution_process_status_id ===
                        DISTRIBUTION_PROCESS_STATUS.PENDING
                    )
                );

                const distributionsByType = {
                  betweenSeller: allPendingDistributions.filter(
                    (p) => p.distribution_type_id === DISTRIBUTION_TYPE.BETWEEN_SELLER
                  ),
                  other: allPendingDistributions.filter(
                    (p) =>
                      !p.distribution_type_id ||
                      p.distribution_type_id !== DISTRIBUTION_TYPE.BETWEEN_SELLER
                  ),
                };

                return (
                  <div className="space-y-3">
                    <p>
                      Você está prestes a aceitar todas as distribuições
                      pendentes.
                    </p>

                    {allPendingDistributions.length > 0 ? (
                      <>
                        <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-800 rounded max-h-96 overflow-y-auto">
                          <p className="text-base font-medium mb-2">
                            Resumo das distribuições (
                            {allPendingDistributions.length} no total):
                          </p>

                          {distributionsByType.betweenSeller.length > 0 && (
                            <div className="mb-2">
                              <p className="text-sm font-medium text-blue-600 dark:text-blue-400">
                                Entre Vendedores (
                                {distributionsByType.betweenSeller.length}):
                              </p>
                              {distributionsByType.betweenSeller.map((product) => (
                                  <p
                                    key={product.id}
                                    className="text-sm text-gray-600 dark:text-gray-300 ml-2"
                                  >
                                    • {product.name} - {product.classification}{" "}
                                    ({product.quotas} cotas)
                                  </p>
                                ))}
                            </div>
                          )}

                          {distributionsByType.other.length > 0 && (
                            <div className="mb-2">
                              <p className="text-sm font-medium text-green-600 dark:text-green-400">
                                Outras Distribuições (
                                {distributionsByType.other.length}):
                              </p>
                              {distributionsByType.other.map((product) => (
                                  <p
                                    key={product.id}
                                    className="text-sm text-gray-600 dark:text-gray-300 ml-2"
                                  >
                                    • {product.name} - {product.classification}{" "}
                                    ({product.quotas} cotas)
                                  </p>
                                ))}
                            </div>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Esta ação processará cada distribuição individualmente
                          usando o endpoint apropriado para cada tipo.
                          Distribuições que falharem serão reportadas
                          separadamente.
                        </p>
                      </>
                    ) : (
                      <div className="mt-3 p-3 bg-yellow-50 dark:bg-yellow-900 rounded">
                        <p className="text-sm text-yellow-800 dark:text-yellow-200">
                          ⚠️ Nenhuma distribuição pendente encontrada.
                        </p>
                      </div>
                    )}
                  </div>
                );
              })()}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowAcceptAllDialog(false)}
              disabled={isAcceptingAll}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleAcceptAll}
              disabled={isAcceptingAll}
              className="bg-green-600 hover:bg-green-700"
            >
              {isAcceptingAll ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" />
                  Aceitando...
                </>
              ) : (
                "Confirmar Aceitação em Lote"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
});
